"use client";
"use no memo";

// React Hook Form watch() returns mutable values that React Compiler cannot safely memoize.
// Remove this boundary when the library publishes compiler-compatible APIs.

import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, Info, MapPin, ShieldCheck, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { GeocodedAddressFields } from "@/src/components/address/geocoded-address-fields";
import type { GeocodedAddressFieldErrors } from "@/src/components/address/types";
import {
  FIELD_CLASSNAME,
  GENDER_OPTIONS,
  isMinorDateOfBirth,
  getPatientDetailsFormSchema,
  type PatientDetailsDraft,
} from "@/src/components/online-scheduling/types";
import { Button } from "@/src/components/shadcn/ui/button";
import { Checkbox } from "@/src/components/shadcn/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/src/components/shadcn/ui/form";
import { Input } from "@/src/components/shadcn/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { ValerieCard } from "@/src/components/valerie-card";
import { onlineSchedulingGeocodingClient } from "@/src/lib/geocoding-client";
import { readOnlineSchedulingInsuranceCard } from "@/src/lib/scheduling/online-scheduling-client";
import { USState } from "@/src/lib/us-states";
import { formatPhoneNumberAsTyped } from "@/src/lib/utils/date-and-phone-utils";

function OptionalHint() {
  return <span className="ml-1 font-normal text-muted-foreground">(optional)</span>;
}

function mapHomeAddressErrors(error: unknown): GeocodedAddressFieldErrors {
  if (!error || typeof error !== "object") {
    return {};
  }

  const record = error as Record<string, { message?: string } | undefined>;

  return {
    line1: record.line1?.message,
    line2: record.line2?.message,
    city: record.city?.message,
    state: record.state?.message,
    postalCode: record.postalCode?.message,
  };
}

export function DetailsStep({
  formId,
  defaultValues,
  onSubmit,
  requireInsurance = false,
  allowSelfPay = false,
  requireExistingPatient = false,
  isRescheduling = false,
  tenant,
}: {
  formId: string;
  defaultValues: PatientDetailsDraft;
  onSubmit: (values: PatientDetailsDraft) => void;
  requireInsurance?: boolean;
  allowSelfPay?: boolean;
  requireExistingPatient?: boolean;
  isRescheduling?: boolean;
  tenant?: string | null;
}) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [isReadingCard, setIsReadingCard] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardMessage, setCardMessage] = useState<string | null>(null);
  const [uploadedCardNames, setUploadedCardNames] = useState<string[]>([]);
  const cardInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<PatientDetailsDraft>({
    resolver: zodResolver(getPatientDetailsFormSchema(requireInsurance, allowSelfPay, isRescheduling)),
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    const subscription = form.watch((_value, { type }) => {
      if (type === "change") {
        form.clearErrors();
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const selfPay = allowSelfPay && form.watch("selfPay");
  const isMinor = isMinorDateOfBirth(form.watch("dateOfBirth"));

  const handleCardUpload = async (files: FileList | null) => {
    const images = Array.from(files ?? []);

    if (images.length === 0) return;
    setCardError(null);
    setCardMessage(null);
    if (images.length > 2 || images.some((file) => file.size > 5 * 1024 * 1024)) {
      setCardError("Choose up to two images, 5 MB or smaller each.");

      return;
    }

    const originalPayer = form.getValues("insurancePayer");
    const originalMemberId = form.getValues("insuranceMemberId");

    setUploadedCardNames([]);
    setIsReadingCard(true);
    try {
      const result = await readOnlineSchedulingInsuranceCard(images, tenant);

      setUploadedCardNames(images.map((image) => image.name));
      if (form.getValues("selfPay")) return;
      if (result.payer && form.getValues("insurancePayer") === originalPayer) {
        form.setValue("insurancePayer", result.payer, { shouldValidate: true });
      }

      if (result.memberId && form.getValues("insuranceMemberId") === originalMemberId) {
        form.setValue("insuranceMemberId", result.memberId, { shouldValidate: true });
      }

      setCardMessage(
        result.payer || result.memberId
          ? "Check the carrier and member ID below against your card before continuing."
          : "We couldn't read the insurance details. Please enter them below."
      );
    } catch {
      setCardError("We couldn't read that card. Please try another image or enter the details below.");
    } finally {
      setIsReadingCard(false);
    }
  };

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={(event) => {
          if (isReadingCard) {
            event.preventDefault();

            return;
          }

          void form.handleSubmit(onSubmit)(event);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
          }
        }}
        className="space-y-6"
        noValidate
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First name</FormLabel>
                <FormControl>
                  <Input autoComplete="given-name" placeholder="Jordan" className={FIELD_CLASSNAME} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last name</FormLabel>
                <FormControl>
                  <Input autoComplete="family-name" placeholder="Rivera" className={FIELD_CLASSNAME} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="dateOfBirth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date of birth</FormLabel>
                <FormControl>
                  <Input type="date" max={todayIso} autoComplete="bday" className={FIELD_CLASSNAME} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile phone</FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="555-123-4567"
                    className={FIELD_CLASSNAME}
                    {...field}
                    onChange={(event) => field.onChange(formatPhoneNumberAsTyped(event.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className={isRescheduling ? "hidden" : "grid grid-cols-1 gap-4 sm:grid-cols-2"}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className={FIELD_CLASSNAME}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Gender</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className={FIELD_CLASSNAME}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {GENDER_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <ValerieCard hidden={isRescheduling} title="Home address" icon={<MapPin aria-hidden />}>
          <FormField
            control={form.control}
            name="homeAddress"
            render={({ field }) => (
              <FormItem>
                <GeocodedAddressFields
                  value={field.value}
                  onChange={field.onChange}
                  geocodingClient={onlineSchedulingGeocodingClient}
                  biasStateCode={USState.TN}
                  inputClassName={FIELD_CLASSNAME}
                  errors={mapHomeAddressErrors(form.formState.errors.homeAddress)}
                  idPrefix="home-address"
                />
              </FormItem>
            )}
          />
        </ValerieCard>

        <ValerieCard
          hidden={requireExistingPatient}
          title={
            <>
              Insurance
              {!requireInsurance && <OptionalHint />}
            </>
          }
          icon={<ShieldCheck aria-hidden />}
          contentClassName="space-y-4"
        >
          {allowSelfPay && (
            <FormField
              control={form.control}
              name="selfPay"
              render={({ field }) => (
                <FormItem className="flex items-start gap-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      className="data-[state=checked]:border-foreground/40 data-[state=checked]:bg-muted data-[state=checked]:text-foreground"
                      checked={field.value ?? false}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">
                    I’m self-pay (I will pay without insurance for this visit)
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {!selfPay && (
            <div className="space-y-2">
              <label htmlFor="insurance-card-images" className="text-sm font-medium">
                Insurance card images <OptionalHint />
              </label>
              <input
                ref={cardInputRef}
                hidden
                id="insurance-card-images"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                disabled={isReadingCard}
                aria-describedby="insurance-card-upload-help"
                onChange={(event) => {
                  void handleCardUpload(event.target.files);
                  event.target.value = "";
                }}
              />
              <div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isReadingCard}
                  aria-label="Choose insurance card images"
                  aria-describedby="insurance-card-upload-help"
                  onClick={() => cardInputRef.current?.click()}
                >
                  Choose files
                </Button>
              </div>
              <p id="insurance-card-upload-help" className="text-xs text-muted-foreground">
                Upload a picture of your insurance card if available (JPG, PNG, WebP, or HEIC; up to 5 MB each).
              </p>
              {isReadingCard && (
                <p role="status" className="text-sm text-muted-foreground">
                  Reading your card...
                </p>
              )}
              {uploadedCardNames.length > 0 && (
                <p role="status" className="flex items-start gap-2 text-sm text-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                    Uploaded: {uploadedCardNames.join(", ")}
                  </span>
                </p>
              )}
              {cardMessage && (
                <p role="status" className="text-sm text-muted-foreground">
                  {cardMessage}
                </p>
              )}
              {cardError && (
                <p role="alert" className="text-sm text-destructive">
                  {cardError}
                </p>
              )}
            </div>
          )}
          {!selfPay && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="insurancePayer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Carrier</FormLabel>
                    <FormControl>
                      <Input
                        aria-required={requireInsurance}
                        placeholder="e.g. Blue Cross Blue Shield"
                        className={FIELD_CLASSNAME}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="insuranceMemberId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Member ID</FormLabel>
                    <FormControl>
                      <Input
                        aria-required={requireInsurance}
                        placeholder="X1234567890"
                        className={FIELD_CLASSNAME}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </ValerieCard>

        {isMinor && (
          <div className="space-y-4 rounded-xl border border-dashed p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold">Parent or legal guardian</span>
            </div>
            <p className="flex items-start gap-2 text-sm text-muted-foreground" role="note">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Patients under 18 must be accompanied by a parent or legal guardian at their appointment.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="guardianFirstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Guardian first name</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" placeholder="Alex" className={FIELD_CLASSNAME} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="guardianLastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Guardian last name</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" placeholder="Rivera" className={FIELD_CLASSNAME} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="guardianPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Guardian phone</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      inputMode="tel"
                      autoComplete="off"
                      placeholder="555-123-4567"
                      className={FIELD_CLASSNAME}
                      {...field}
                      onChange={(event) => field.onChange(formatPhoneNumberAsTyped(event.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </form>
    </Form>
  );
}
